import * as THREE from 'three';

/**
 * Procedural Mixamo-compatible AnimationClips for sword combat.
 * Tracks use mixamorig:* bone names (fencer-rigged.glb / Soldier / Xbot).
 *
 * Locomotion + most combat clips live on fencer-rigged.glb; these fill
 * gaps (EnGarde) and act as fallback when a GLB track is missing.
 */

function eulerTrack(
  bone: string,
  keys: { t: number; x: number; y: number; z: number }[],
  order: THREE.EulerOrder = 'XYZ',
): THREE.QuaternionKeyframeTrack {
  const times: number[] = [];
  const values: number[] = [];
  const e = new THREE.Euler();
  const q = new THREE.Quaternion();
  for (const k of keys) {
    times.push(k.t);
    e.set(k.x, k.y, k.z, order);
    q.setFromEuler(e);
    values.push(q.x, q.y, q.z, q.w);
  }
  return new THREE.QuaternionKeyframeTrack(`${bone}.quaternion`, times, values);
}

const B = {
  hips: 'mixamorig:Hips',
  spine: 'mixamorig:Spine',
  spine1: 'mixamorig:Spine1',
  spine2: 'mixamorig:Spine2',
  rShoulder: 'mixamorig:RightShoulder',
  rArm: 'mixamorig:RightArm',
  rFore: 'mixamorig:RightForeArm',
  rHand: 'mixamorig:RightHand',
  lArm: 'mixamorig:LeftArm',
  lFore: 'mixamorig:LeftForeArm',
  rUpLeg: 'mixamorig:RightUpLeg',
  rLeg: 'mixamorig:RightLeg',
  lUpLeg: 'mixamorig:LeftUpLeg',
  lLeg: 'mixamorig:LeftLeg',
};

/** En garde arm pose (radians offsets roughly matching Mixamo bind). */
const GUARD = {
  rArm: { x: -0.9, y: 0.25, z: 0.35 },
  rFore: { x: -0.45, y: 0, z: 0.1 },
  rHand: { x: 0.1, y: 0, z: 0 },
  lArm: { x: -0.85, y: -0.2, z: -0.4 },
  lFore: { x: -0.55, y: 0, z: 0 },
};

export function createCombatClips(): THREE.AnimationClip[] {
  return [
    createLungeClip(),
    createAttackClip(),
    createParryHighClip(),
    createParryLowClip(),
    createFlecheClip(),
    createEnGardeClip(),
  ];
}

function createEnGardeClip(): THREE.AnimationClip {
  // Looping micro-motion en garde for when idle locomotion is weak
  const d = 1.6;
  const tracks = [
    eulerTrack(B.rArm, [
      { t: 0, ...GUARD.rArm },
      { t: d * 0.5, x: GUARD.rArm.x + 0.04, y: GUARD.rArm.y, z: GUARD.rArm.z },
      { t: d, ...GUARD.rArm },
    ]),
    eulerTrack(B.rFore, [
      { t: 0, ...GUARD.rFore },
      { t: d * 0.5, x: GUARD.rFore.x - 0.03, y: 0, z: GUARD.rFore.z },
      { t: d, ...GUARD.rFore },
    ]),
    eulerTrack(B.lArm, [
      { t: 0, ...GUARD.lArm },
      { t: d * 0.5, x: GUARD.lArm.x + 0.03, y: GUARD.lArm.y, z: GUARD.lArm.z },
      { t: d, ...GUARD.lArm },
    ]),
  ];
  return new THREE.AnimationClip('Combat_EnGarde', d, tracks);
}

function createLungeClip(): THREE.AnimationClip {
  // Classic fencing lunge: coil → explode extension → recover start
  const d = 0.55;
  const tracks = [
    eulerTrack(B.rArm, [
      { t: 0, x: -0.7, y: 0.2, z: 0.3 },
      { t: 0.12, x: -0.55, y: 0.15, z: 0.25 },
      { t: 0.28, x: -1.75, y: 0.05, z: 0.05 },
      { t: 0.45, x: -1.7, y: 0.05, z: 0.05 },
      { t: d, x: -1.2, y: 0.15, z: 0.15 },
    ]),
    eulerTrack(B.rFore, [
      { t: 0, x: -0.6, y: 0, z: 0.1 },
      { t: 0.28, x: -0.05, y: 0, z: 0 },
      { t: d, x: -0.25, y: 0, z: 0 },
    ]),
    eulerTrack(B.rHand, [
      { t: 0, x: 0.1, y: 0, z: 0 },
      { t: 0.28, x: 0.0, y: 0, z: 0 },
      { t: d, x: 0.05, y: 0, z: 0 },
    ]),
    eulerTrack(B.spine1, [
      { t: 0, x: 0, y: 0, z: 0 },
      { t: 0.28, x: 0.25, y: 0, z: 0.08 },
      { t: d, x: 0.12, y: 0, z: 0.04 },
    ]),
    eulerTrack(B.rUpLeg, [
      { t: 0, x: 0.1, y: 0, z: 0 },
      { t: 0.3, x: 1.05, y: 0, z: 0 },
      { t: d, x: 0.7, y: 0, z: 0 },
    ]),
    eulerTrack(B.rLeg, [
      { t: 0, x: 0, y: 0, z: 0 },
      { t: 0.3, x: -0.55, y: 0, z: 0 },
      { t: d, x: -0.3, y: 0, z: 0 },
    ]),
    eulerTrack(B.lUpLeg, [
      { t: 0, x: 0, y: 0, z: 0 },
      { t: 0.3, x: -0.45, y: 0, z: 0 },
      { t: d, x: -0.25, y: 0, z: 0 },
    ]),
    eulerTrack(B.lArm, [
      { t: 0, ...GUARD.lArm },
      { t: 0.3, x: -1.2, y: -0.3, z: -0.5 },
      { t: d, x: -1.0, y: -0.25, z: -0.45 },
    ]),
  ];
  return new THREE.AnimationClip('Combat_Lunge', d, tracks);
}

function createAttackClip(): THREE.AnimationClip {
  // Quick thrust / cut for LMB (slightly snappier than full lunge)
  const d = 0.42;
  const tracks = [
    eulerTrack(B.rArm, [
      { t: 0, x: -0.85, y: 0.2, z: 0.3 },
      { t: 0.08, x: -0.5, y: 0.35, z: 0.4 },
      { t: 0.2, x: -1.85, y: 0.0, z: 0.0 },
      { t: 0.32, x: -1.7, y: 0.05, z: 0.05 },
      { t: d, x: -1.0, y: 0.2, z: 0.25 },
    ]),
    eulerTrack(B.rFore, [
      { t: 0, x: -0.5, y: 0, z: 0.1 },
      { t: 0.2, x: 0.05, y: 0.1, z: 0 },
      { t: d, x: -0.35, y: 0, z: 0.1 },
    ]),
    eulerTrack(B.spine2, [
      { t: 0, x: 0, y: 0, z: 0 },
      { t: 0.2, x: 0.18, y: 0.15, z: 0.05 },
      { t: d, x: 0.05, y: 0.05, z: 0 },
    ]),
    eulerTrack(B.rUpLeg, [
      { t: 0, x: 0.05, y: 0, z: 0 },
      { t: 0.22, x: 0.55, y: 0, z: 0 },
      { t: d, x: 0.15, y: 0, z: 0 },
    ]),
  ];
  return new THREE.AnimationClip('Combat_Attack', d, tracks);
}

function createParryHighClip(): THREE.AnimationClip {
  const d = 0.38;
  const tracks = [
    eulerTrack(B.rArm, [
      { t: 0, ...GUARD.rArm },
      { t: 0.1, x: -0.2, y: 0.75, z: 0.65 },
      { t: 0.25, x: -0.15, y: 0.85, z: 0.7 },
      { t: d, x: -0.4, y: 0.5, z: 0.45 },
    ]),
    eulerTrack(B.rFore, [
      { t: 0, ...GUARD.rFore },
      { t: 0.12, x: -1.25, y: 0.2, z: 0.3 },
      { t: d, x: -0.8, y: 0.1, z: 0.15 },
    ]),
    eulerTrack(B.rHand, [
      { t: 0, x: 0, y: 0, z: 0 },
      { t: 0.15, x: 0.3, y: 0.4, z: 0.2 },
      { t: d, x: 0.1, y: 0.15, z: 0.1 },
    ]),
    eulerTrack(B.spine1, [
      { t: 0, x: 0, y: 0, z: 0 },
      { t: 0.15, x: -0.05, y: -0.12, z: -0.08 },
      { t: d, x: 0, y: -0.05, z: -0.03 },
    ]),
  ];
  return new THREE.AnimationClip('Combat_ParryHigh', d, tracks);
}

function createParryLowClip(): THREE.AnimationClip {
  const d = 0.38;
  const tracks = [
    eulerTrack(B.rArm, [
      { t: 0, ...GUARD.rArm },
      { t: 0.12, x: -1.35, y: -0.55, z: 0.4 },
      { t: 0.25, x: -1.4, y: -0.65, z: 0.45 },
      { t: d, x: -1.1, y: -0.35, z: 0.3 },
    ]),
    eulerTrack(B.rFore, [
      { t: 0, ...GUARD.rFore },
      { t: 0.15, x: -0.85, y: -0.2, z: 0.15 },
      { t: d, x: -0.55, y: -0.1, z: 0.1 },
    ]),
    eulerTrack(B.spine1, [
      { t: 0, x: 0, y: 0, z: 0 },
      { t: 0.15, x: 0.12, y: 0.1, z: 0.08 },
      { t: d, x: 0.04, y: 0.04, z: 0.03 },
    ]),
  ];
  return new THREE.AnimationClip('Combat_ParryLow', d, tracks);
}

function createFlecheClip(): THREE.AnimationClip {
  const d = 0.5;
  const tracks = [
    eulerTrack(B.rArm, [
      { t: 0, x: -0.9, y: 0.2, z: 0.3 },
      { t: 0.15, x: -1.9, y: 0, z: 0 },
      { t: 0.4, x: -1.85, y: 0, z: 0 },
      { t: d, x: -1.3, y: 0.1, z: 0.15 },
    ]),
    eulerTrack(B.rFore, [
      { t: 0, x: -0.4, y: 0, z: 0 },
      { t: 0.2, x: 0.1, y: 0, z: 0 },
      { t: d, x: -0.2, y: 0, z: 0 },
    ]),
    eulerTrack(B.spine1, [
      { t: 0, x: 0, y: 0, z: 0 },
      { t: 0.25, x: 0.4, y: 0, z: 0.12 },
      { t: d, x: 0.2, y: 0, z: 0.06 },
    ]),
    eulerTrack(B.rUpLeg, [
      { t: 0, x: 0.1, y: 0, z: 0 },
      { t: 0.25, x: 1.15, y: 0, z: 0 },
      { t: d, x: 0.6, y: 0, z: 0 },
    ]),
    eulerTrack(B.lUpLeg, [
      { t: 0, x: 0, y: 0, z: 0 },
      { t: 0.25, x: -0.6, y: 0, z: 0 },
      { t: d, x: -0.3, y: 0, z: 0 },
    ]),
  ];
  return new THREE.AnimationClip('Combat_Fleche', d, tracks);
}

/** Remap clip tracks if model uses bone names without mixamorig: prefix. */
export function retargetClipToSkeleton(
  clip: THREE.AnimationClip,
  root: THREE.Object3D,
): THREE.AnimationClip {
  const nameSet = new Set<string>();
  root.traverse((o) => {
    if ((o as THREE.Bone).isBone || o.type === 'Bone') nameSet.add(o.name);
  });
  if (nameSet.has('mixamorig:RightArm') || nameSet.has('mixamorigRightArm')) {
    return clip;
  }
  // Try stripping mixamorig:
  const tracks = clip.tracks.map((track) => {
    const [node, prop] = [track.name.split('.')[0], track.name.split('.').slice(1).join('.')];
    const short = node.replace(/^mixamorig:/, '');
    let target = node;
    if (nameSet.has(short)) target = short;
    else {
      for (const n of nameSet) {
        if (n.endsWith(short) || n.replace(/[:_]/g, '').endsWith(short)) {
          target = n;
          break;
        }
      }
    }
    const name = `${target}.${prop}`;
    if (track instanceof THREE.QuaternionKeyframeTrack) {
      return new THREE.QuaternionKeyframeTrack(name, Array.from(track.times), Array.from(track.values));
    }
    if (track instanceof THREE.VectorKeyframeTrack) {
      return new THREE.VectorKeyframeTrack(name, Array.from(track.times), Array.from(track.values));
    }
    return track;
  });
  return new THREE.AnimationClip(clip.name, clip.duration, tracks);
}
